'use client';

import { EmojiPicker, type Locale } from 'frimousse';
import { useLocale, useTranslations } from 'next-intl';
import { useRef, useState } from 'react';
import { cn } from '@/lib/ui-kit/utils';
import { SearchShell } from './SearchShell';

interface EmojiPanelProps {
  onSelect: (emoji: string) => void;
}

// Map app locales to frimousse-supported ones. Indonesian (`id`) has no
// emojibase dataset, so fall back to English emoji labels/search.
const EMOJI_LOCALE: Record<string, Locale> = {
  en: 'en',
  es: 'es',
  pt: 'pt',
  zh: 'zh',
  ja: 'ja',
  ko: 'ko',
  de: 'de',
  id: 'en',
};

// Emoji tab of the unified picker. frimousse is headless: it owns emoji data
// (emojibase via CDN), search, virtualization and recents — we own all chrome.
export const EmojiPanel = ({ onSelect }: EmojiPanelProps) => {
  const t = useTranslations('gifPicker');
  const locale = useLocale();
  const inputRef = useRef<HTMLInputElement>(null);
  const [focused, setFocused] = useState(false);
  // Typed search text. A picked mood drives the (controlled) frimousse search
  // separately so the visible field stays clean while the mood stays active.
  const [text, setText] = useState('');
  const [activeMood, setActiveMood] = useState<string | null>(null);

  const searchValue = activeMood ?? text;
  const handleChange = (value: string) => {
    setText(value);
    if (activeMood) setActiveMood(null);
  };
  const handleFocus = () => {
    setFocused(true);
    // Focusing to type starts a clean field — drop any active mood filter.
    if (activeMood) {
      setActiveMood(null);
      setText('');
    }
  };
  const selectMood = (mood: string) => {
    setActiveMood((prev) => (prev === mood ? null : mood));
    setText('');
  };

  return (
    <EmojiPicker.Root
      locale={EMOJI_LOCALE[locale] ?? 'en'}
      columns={8}
      onEmojiSelect={({ emoji }) => onSelect(emoji)}
      className="flex h-[325px] flex-col"
    >
      <SearchShell
        showMoods={!focused && text.length === 0}
        activeMood={activeMood}
        onMood={selectMood}
        onSearchIconClick={() => inputRef.current?.focus()}
        onBack={() => setActiveMood(null)}
      >
        <EmojiPicker.Search
          ref={inputRef}
          value={searchValue}
          onChange={(e) => handleChange(e.target.value)}
          onFocus={handleFocus}
          onBlur={() => setFocused(false)}
          placeholder={t('searchEmoji')}
          className={cn(
            'min-w-[52px] flex-1 bg-transparent text-[13px] text-v2-text-primary outline-none placeholder:text-v2-text-secondary',
            // Mood drives the (controlled) value for filtering; the input yields
            // the row to the arrow + moods while one is active (per design).
            activeMood && 'hidden',
          )}
        />
      </SearchShell>
      <EmojiPicker.Viewport className="relative mt-3 flex-1 outline-none">
        <EmojiPicker.Loading className="absolute inset-0 flex items-center justify-center text-[13px] text-v2-text-secondary">
          {t('loading')}
        </EmojiPicker.Loading>
        <EmojiPicker.Empty className="absolute inset-0 flex items-center justify-center text-[13px] text-v2-text-secondary">
          {t('noEmoji')}
        </EmojiPicker.Empty>
        <EmojiPicker.List
          className="select-none pb-1.5"
          components={{
            CategoryHeader: ({ category, ...props }) => (
              <div
                className="bg-v2-surface-secondary pt-2 pb-1.5 text-[13px] text-v2-text-secondary uppercase"
                {...props}
              >
                {category.label}
              </div>
            ),
            Row: ({ children, ...props }) => (
              <div className="flex" {...props}>
                {children}
              </div>
            ),
            Emoji: ({ emoji, ...props }) => (
              <button
                type="button"
                className="flex size-9 cursor-pointer items-center justify-center rounded-[8px] text-[22px] leading-none data-[active]:bg-v2-surface-tertiary"
                {...props}
              >
                {emoji.emoji}
              </button>
            ),
          }}
        />
      </EmojiPicker.Viewport>
    </EmojiPicker.Root>
  );
};
